import { useRollbar } from '@rollbar/react'
import * as pageActions from 'client/actions/pageActions'
import { savePageAutomationRulesData } from 'client/api/pageApi'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import { useUnsavedData } from 'client/hooks/useUnsavedData'
import {
  pageSelectors,
  useAutomationRules,
  usePage,
  useWebsiteSettings,
} from 'client/store'
import { fetchPageAutomationRules } from 'client/store/automationRules/automationRulesActions'
import automationRulesSelectors from 'client/store/automationRules/automationRulesSelectors'
import { AccessDenied } from 'common/errors/AccessDenied'
import { BadRequest } from 'common/errors/BadRequest'
import { NetworkError } from 'common/errors/NetworkError'
import { NotFound } from 'common/errors/NotFound'
import { PageValidationError } from 'common/errors/PageValidationError'
import { PageValidationErrorNew } from 'common/errors/PageValidationErrorNew'
import { useActions } from 'common/hooks/useActions'
import React, { memo, useState } from 'react'
import toast from 'react-hot-toast'
import { useDispatch } from 'react-redux'
import { LogArgument } from 'rollbar'
import { useWebsiteSave } from 'src/editor/hooks/use-website-save'
import { useWhiteLabelBrandSettings } from 'src/editor/hooks/use-white-label-brand'
import { transformAutomationRulesForApi } from 'src/editor/shared/settings/automation-rules-settings/utils/transform-automation-rule-for-api'
import { TertiaryButton } from 'src/editor/shared/ui-lib/buttons'
import { getWebsiteId } from 'src/editor/store/website-settings/website-settings-selector'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { twMerge } from 'tailwind-merge'
import { saveWebsitePage } from '../../api/website-pages-api'
import { useWebsitePageSavePayload } from '../../hooks/use-website-page-save-payload'

export const SavePageButton = memo(() => {
  const { t, i18n } = useTypedTranslation()
  const rollbar = useRollbar()
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useDispatch()
  const { isTemplate } = usePageRouteDetector()
  const savePage = useActions(pageActions.savePage)
  const automationRules = useAutomationRules(
    automationRulesSelectors.getAutomationRules,
  )
  const pageId = usePage(pageSelectors.getPageId)
  const { brandSettings } = useWhiteLabelBrandSettings()

  const { deleteDbRecord, PageUnsavedModal } = useUnsavedData({})

  const websiteId = useWebsiteSettings(getWebsiteId)
  const pageSavePayload = useWebsitePageSavePayload()
  const saveWebsiteData = useWebsiteSave()

  async function handleSave() {
    setIsLoading(true)
    try {
      if (websiteId && pageSavePayload) {
        await saveWebsitePage(pageSavePayload)
        await saveWebsiteData(websiteId)
      } else {
        await savePage(isTemplate)
      }
      dispatch(pageActions.savePageSuccess())
      await deleteDbRecord()
      if (!isTemplate) {
        try {
          const response = await savePageAutomationRulesData(
            pageId,
            transformAutomationRulesForApi(automationRules),
          )
          dispatch(fetchPageAutomationRules(response.data.rules))
        } catch (e) {
          if (e instanceof BadRequest) {
            const commonErrors = e.response.data.errors?.common
            if (commonErrors.length !== 0) {
              toast.error(commonErrors.join(''))
            }
          } else if (e instanceof AccessDenied) {
            // do nothing
          } else if (e instanceof NetworkError) {
            // just show warning about data loss
            dispatch(pageActions.savePageFail('core.errors.save'))
          } else {
            rollbar.error('Save page automation rules error', e as LogArgument)
          }
        }
      }
    } catch (e) {
      if (e instanceof PageValidationError) {
        dispatch(pageActions.pageValidationFail(e.messages))
      } else if (e instanceof PageValidationErrorNew) {
        toast.error(e.message)
      } else if (e instanceof NotFound) {
        dispatch(pageActions.savePageFail('core.errors.page_not_found'))
      } else if (e instanceof AccessDenied) {
        // do nothing
      } else if (e instanceof NetworkError) {
        // just show warning about data loss
        dispatch(pageActions.savePageFail('core.errors.save'))
      } else if (e instanceof BadRequest) {
        dispatch(pageActions.pageValidationFail(e.response.data))
      } else {
        dispatch(pageActions.savePageFail('core.errors.save'))
      }
    }
    setIsLoading(false)
  }

  return (
    <React.Fragment>
      <TertiaryButton
        disabled={!i18n.language || isLoading || !pageId}
        onClick={handleSave}
        title={t('core.header.save_changes')}
        data-test-element="button-save-changes"
        hasSkeleton={!i18n.language}
        isLoading={isLoading}
        loaderProps={{
          dataTestElement: 'save-spinner',
        }}
        className={twMerge(
          brandSettings?.colors &&
            'bg-brandPrimary text-white hover:bg-brandPrimaryHover active:bg-brandPrimaryPressed',
        )}
      >
        {t('core.header.save')}
      </TertiaryButton>
      {PageUnsavedModal}
    </React.Fragment>
  )
})
